import manageSegments from "./ManageSegments";
import setupProfile from "../cdp/SetupProfile";
import { DataSyncLibEventNames } from "../shared/DataSyncLibEventNames";
import { Logger } from "../utils/Logger";
import { PageAttributesExtractor } from "../shared/PageAttributesExtractor";
import {
  invokeWhenProfileLoaded,
  invokeWhenProfileSetup,
  shouldStartNewWebSession,
  updateProfileProperties,
} from "./BlueConicUtils";
import { inject, singleton } from "tsyringe";
import { ServiceTokens } from "../shared/ServiceTokens";
import { SiteRules } from "../shared/SiteRules";
import { PageAttributes } from "../shared/PageAttributes";
import { StreamMediaType } from "../shared/StreamMediaType";
import { TimelineEventIds } from "./TimelineEventsId";
import isEqual from "lodash/isEqual";
import { SiteSettings } from "../shared/SiteSettings";
import { getOrCreateUserAdapter } from "../gtm/GtmUtils";
import { BlueConicProfileProperties } from "./BlueConicProfileProperties";
import { BlueConicProfile } from "./blueConicJsApi/BlueConicProfile";
import { PropertyValuePair } from "./PropertyValuePair";
import { CmpService } from "../cmp/CmpService";

/**
 * Base CDP client implementation
 */
@singleton()
export class CdpClient {
  constructor(
    @inject(ServiceTokens.PageAttributesExtractor) protected pageAttributesExtractor: PageAttributesExtractor,
    @inject(SiteRules) protected siteRules: SiteRules,
    @inject(CmpService) protected cmpService: CmpService,
    @inject(ServiceTokens.SiteSettings) protected siteSettings: SiteSettings,
  ) {}

  private lastReadJsPageAttributes?: PageAttributes;
  private lastRcLoadedPageAttributes?: PageAttributes;
  private lastRcStreamingPageAttributes?: PageAttributes;

  /**
   * Using the current state of the user in the DataLayer, load and set up the CDP profile.
   * */
  public async initialize(): Promise<void> {
    if (!this.siteRules.isGTMLibraryEnabled) {
      Logger.verbose("CDP : excluded page or host. BlueConic script will not be loaded.");
      return;
    }
    invokeWhenProfileLoaded(() => {
      setupProfile(this, () => {
        window.dispatchEvent(new Event(DataSyncLibEventNames.CdpProfileSetup));
      });
    });
  }

  /**
   * Invoke this method when a new page view is registered on a SPA.
   * @returns Returns true if the Page View event was handled. False otherwise.
   */
  public async handlePageView(): Promise<boolean> {
    if (!this.siteRules.isGTMLibraryEnabled) {
      return false;
    }
    await this.sendRcStartWebSession();
    this.sendRcLoaded();
    invokeWhenProfileSetup(() => manageSegments(this.cmpService.UserConsent));
    // BlueConic Library generate their own page view event via their own listener
    return true;
  }

  /**
   * Send the RC_Read timeline event to the CDP.
   */
  public sendRcRead = (): void => {
    if (!this.siteRules.isGTMLibraryEnabled) {
      return;
    }
    // Immediately capture the page attributes in case of page change.
    const pageAttributes = this.pageAttributesExtractor.extractReadAttributes();
    if (!this.lastReadJsPageAttributes || !isEqual(this.lastReadJsPageAttributes, pageAttributes)) {
      this.lastReadJsPageAttributes = pageAttributes;
      const now = new Date();
      invokeWhenProfileSetup(() => {
        Logger.verbose(`Sending ${TimelineEventIds.RcRead}`, pageAttributes);
        const profile = blueConicClient.profile.getProfile();
        profile.timeline.createEvent(TimelineEventIds.RcRead, now, pageAttributes, this);
        updateProfileProperties([
          {
            propertyId: BlueConicProfileProperties.rcLastReadEventDate,
            value: now.valueOf().toString(),
          },
        ]);
      });
    }
  };

  /**
   * Send either the Rc_Streamed_Audio or Rc_Stream_Video
   * @param mediaType Content type, either video or audio.
   */
  public async sendRcStream(mediaType: StreamMediaType): Promise<void> {
    if (!this.siteRules.isGTMLibraryEnabled) {
      return;
    }
    const now = new Date();
    await this.sendRcStartWebSession();
    const eventTypeName =
      mediaType === StreamMediaType.Audio ? TimelineEventIds.RcStreamedAudio : TimelineEventIds.RcStreamedVideo;
    // Immediately capture the page attributes in case of page change.
    const pageAttributes = this.pageAttributesExtractor.extractStreamAttributes(mediaType);
    if (!this.lastRcStreamingPageAttributes || !isEqual(this.lastRcStreamingPageAttributes, pageAttributes)) {
      this.lastRcStreamingPageAttributes = pageAttributes;
      invokeWhenProfileSetup(() => {
        Logger.verbose(`Sending ${eventTypeName}`, pageAttributes);
        blueConicClient.profile.getProfile().timeline.createEvent(eventTypeName, now, pageAttributes, undefined);
        switch (eventTypeName) {
          case TimelineEventIds.RcStreamedAudio:
            updateProfileProperties([
              {
                propertyId: BlueConicProfileProperties.rcLastStartStreamedAudioEventDate,
                value: now.valueOf().toString(),
              },
            ]);
            break;
          case TimelineEventIds.RcStreamedVideo:
            updateProfileProperties([
              {
                propertyId: BlueConicProfileProperties.rcLastStartStreamedVideoEventDate,
                value: now.valueOf().toString(),
              },
            ]);
            break;
          default:
            Logger.error("Unhandled event type", eventTypeName);
            break;
        }
      });
    }
  }

  /**
   * Update the profile with the consent status.
   */
  public processConsentChange(): void {
    if (!this.siteRules.isGTMLibraryEnabled) {
      return;
    }

    const consent = this.cmpService.UserConsent;

    if (consent.targetedAds === undefined && consent.targetedContent === undefined) {
      Logger.verbose("Consent values are undefined, skipping Blueconic profile update");
      return;
    }

    const consentProfileProperties: PropertyValuePair[] = [];

    if (consent.targetedAds !== undefined) {
      consentProfileProperties.push({
        propertyId: BlueConicProfileProperties.targetedAds,
        value: String(consent.targetedAds),
      });
    }

    if (consent.targetedContent !== undefined) {
      consentProfileProperties.push({
        propertyId: BlueConicProfileProperties.targetedContent,
        value: String(consent.targetedContent),
      });
    }

    invokeWhenProfileSetup(() => {
      Logger.verbose("Updating profile with consent changes", consent);
      updateProfileProperties(consentProfileProperties);
      manageSegments(consent);
    });
  }

  /**
   * Send the RC_Loaded timeline event to the CDP.
   */
  private sendRcLoaded = (): void => {
    if (!this.siteRules.isGTMLibraryEnabled) {
      return;
    }
    // Immediately capture the page attributes in case of page change.
    const pageAttributes = this.pageAttributesExtractor.extractPageLoadedAttributes();
    if (!this.lastRcLoadedPageAttributes || !isEqual(this.lastRcLoadedPageAttributes, pageAttributes)) {
      this.lastRcLoadedPageAttributes = pageAttributes;
      const now = new Date();
      invokeWhenProfileSetup(() => {
        Logger.verbose(`Sending ${TimelineEventIds.RcLoaded}`, pageAttributes);
        const profile = blueConicClient.profile.getProfile();
        profile.timeline.createEvent(TimelineEventIds.RcLoaded, now, pageAttributes, undefined);
        updateProfileProperties([
          {
            propertyId: BlueConicProfileProperties.rcLastLoadedEventDate,
            value: now.valueOf().toString(),
          },
        ]);
      });
    }
  };

  /**
   * Send the RcStartWebSession event to the CDP if a new session has started or the previous session has expired
   * @private
   */
  private async sendRcStartWebSession(): Promise<void> {
    if (!this.siteRules.isGTMLibraryEnabled) {
      return;
    }

    // Collect the page attributes for the start session event as early as possible in case of page change.
    const pageAttributes = await this.pageAttributesExtractor.extractStartSessionAttributes();
    const now = new Date();
    invokeWhenProfileSetup(async (): Promise<void> => {
      if (navigator.locks !== undefined) {
        await navigator.locks.request("bc_startWebSession", async () => {
          this.createWebSessionIfRequired(pageAttributes, now);
        });
      } else {
        Logger.warn(
          "Locks API not supported. Starting web session without lock. This may cause multiple sessions to be created.",
        );
        this.createWebSessionIfRequired(pageAttributes, now);
      }
    });
  }

  private createWebSessionIfRequired(pageAttributes: PageAttributes, now: Date): void {
    if (!shouldStartNewWebSession(this.siteSettings.WebSessionLengthInMinutes)) {
      return;
    }
    this.logSendRcStartWebSession();
    Logger.verbose(`Sending ${TimelineEventIds.RcStartWebSession}`, pageAttributes); // Used in tests!
    const profile = blueConicClient.profile.getProfile();
    profile.timeline.createEvent(TimelineEventIds.RcStartWebSession, now, pageAttributes, undefined);
    let propertyValuePairs: PropertyValuePair[] = [
      {
        propertyId: BlueConicProfileProperties.rcLastStartWebSessionEventDate,
        value: now.valueOf().toString(),
      },
    ];
    if (pageAttributes.postalCode) {
      const postalCodeGroupId = this.getPostalCodeGroupId(pageAttributes.postalCode, profile);
      const fsa = postalCodeGroupId.slice(0, 3);
      propertyValuePairs = propertyValuePairs.concat([
        {
          propertyId: BlueConicProfileProperties.postalCodeMostRecent,
          value: pageAttributes.postalCode,
        },
        {
          propertyId: BlueConicProfileProperties.postalCodeGroupId,
          value: postalCodeGroupId,
        },
        {
          propertyId: BlueConicProfileProperties.fsaGroupId,
          value: fsa,
        },
      ]);
    } else {
      Logger.verbose(`CDP: No postal code detected in the page attributes. Skipping postal code update.`);
    }

    updateProfileProperties(propertyValuePairs);
  }

  private getPostalCodeGroupId(mostRecentPostalCode: string, profile: BlueConicProfile): string {
    const postalCodeDeclared = profile.getValue(BlueConicProfileProperties.postalCodeDeclared);
    Logger.verbose(`CDP: Postal code declared: ${postalCodeDeclared}`);
    if (postalCodeDeclared) {
      return postalCodeDeclared;
    }
    const postalCodeRegion = profile.getValue(BlueConicProfileProperties.postalCodeRegion);
    Logger.verbose(`CDP: Postal code region: ${postalCodeRegion}`);
    if (postalCodeRegion) {
      return postalCodeRegion;
    }
    const postalCodeMostFrequent = profile.getValue(BlueConicProfileProperties.postalCodeMostFrequent);
    Logger.verbose(`CDP: Postal code most frequent: ${postalCodeMostFrequent}`);
    if (postalCodeMostFrequent) {
      return postalCodeMostFrequent;
    }
    Logger.verbose(`CDP: Postal code most recent: ${mostRecentPostalCode}`);
    return mostRecentPostalCode;
  }

  private logSendRcStartWebSession(): void {
    const dataLayerUser = getOrCreateUserAdapter();
    if (dataLayerUser.rcId) {
      Logger.verbose(`${TimelineEventIds.RcStartWebSession} event with RcId: ${dataLayerUser.rcId}`);
    } else if (dataLayerUser.rc_visitor_id) {
      Logger.verbose(`${TimelineEventIds.RcStartWebSession} event with RcVisitorId: ${dataLayerUser.rc_visitor_id}`);
    }
  }
}
